import moment from 'moment/moment'

import { DataType } from '../types/DataType'
import { ConstructorArgs } from '../types/models'
import { BaseRecord } from './BaseRecord'
import { Booking } from './Booking'
import { Image } from './Image'
import { Invoice } from './Invoice'
import { Lesson } from './Lesson'
import { Line } from './Line'
import { Option } from './Option'
import { Package } from './Package'
import { RecordFactory } from './RecordFactory'
import { FKType } from './SQLBuilder'
import { Subaccount } from './Subaccount'
import { Subscription } from './Subscription'
import { Transactable } from './Transactable'
import { User } from './User'

export type JobConstructorArgs = ConstructorArgs<() => Job>

export class Job extends BaseRecord implements Transactable {
    readonly recordType = 'job'

    readonly iTransactable = true

    total?: number
    amount?: number
    base?: number
    instances?: number // Integer
    rate?: number
    rateType?: string
    type?: string
    subtype: 'checkin' | 'checkout' | null
    status?: string
    refnum?: string
    paymeth?: string
    duration?: number // Integer

    /* checkin/checkout fields */
    checkinDate?: moment.Moment
    checkoutDate?: moment.Moment
    checkinImage?: Image
    checkoutImage?: Image
    checkinLatitude?: number
    checkinLongitude?: number
    checkoutLatitude?: number
    checkoutLongitude?: number
    allowCheckin?: boolean

    date?: moment.Moment
    confirmedDate?: moment.Moment
    paidDate?: moment.Moment
    markPaidDate?: moment.Moment
    cancelledDate?: moment.Moment
    availableDate?: moment.Moment
    lastModifiedDate?: moment.Moment

    constructor(data?: Partial<JobConstructorArgs>) {
        super(data)
        if (!data) {
            this.subtype = null
            return
        }

        this.status = data.status ?? undefined
        this.total = this.propertyFromCurrencyArg(data.total)
        this.amount = this.propertyFromCurrencyArg(data.amount)
        this.date = this.propertyFromDateArg(data.date)
        this.instances = this.propertyFromIntArg(data.instances)
        this.base = this.propertyFromCurrencyArg(data.base)
        this.rate = this.propertyFromCurrencyArg(data.rate)
        this.rateType = data.rate_type ?? undefined
        this.duration = this.propertyFromIntArg(data.duration)
        this.type = data.type ?? undefined
        this.subtype = data.subtype ?? null
        this.refnum = data.refnum ?? undefined
        this.paymeth = data.paymeth ?? undefined
        this.paidDate = this.propertyFromTimestampArg(data.paid_date)
        this.markPaidDate = this.propertyFromTimestampArg(data.mark_paid_date)
        this.allowCheckin = data.allow_checkin ?? false
        this.checkinDate = this.propertyFromTimestampArg(data.checkin_date)
        this.checkoutDate = this.propertyFromTimestampArg(data.checkout_date)
        this.checkinLatitude = this.propertyFromNumberArg(data.checkin_latitude)
        this.checkoutLatitude = this.propertyFromNumberArg(data.checkout_latitude)
        this.checkinLongitude = this.propertyFromNumberArg(data.checkin_longitude)
        this.checkoutLongitude = this.propertyFromNumberArg(data.checkout_longitude)
        this.checkinImage = this.propertyFromImageArg(data.checkin_image, data.checkin_image_uri)
        this.checkoutImage = this.propertyFromImageArg(data.checkout_image, data.checkout_image_uri)
        this.confirmedDate = this.propertyFromTimestampArg(data.confirmed_date)
        this.cancelledDate = this.propertyFromTimestampArg(data.cancelled_date)
        this.availableDate = this.propertyFromDateArg(data.available_date)
        this.lastModifiedDate = this.propertyFromTimestampArg(data.last_modified_date)
    }

    clone(overrides?: Partial<JobConstructorArgs>): Job {
        return new Job({ ...this.serializeForClone(), ...overrides })
    }

    static initWithPerson(person: User): Job {
        const job = new Job()
        job.person = person
        return job
    }

    get employee(): User | undefined {
        return this.person
    }

    get subaccount(): Subaccount | undefined {
        if (this.booking) return this.booking.subaccount
        if (this.invoice) return this.invoice.subaccount
        if (this.aPackage) return this.aPackage.subaccount
        if (this.subscription) return this.subscription.subaccount
        return undefined
    }

    get paidAmount(): number | undefined {
        return this.paidDate !== undefined ? this.amount : 0
    }

    get owedAmount(): number | undefined {
        return this.paidDate !== undefined ? 0 : this.amount
    }

    get taxAmount(): number {
        return 0
    }

    get taxableAmount(): number {
        return 0
    }

    get discountableTotal(): number {
        return 0
    }

    get lines(): Line[] {
        return []
    }

    get statusString(): string {
        if (this.markPaidDate) return `Marked paid ${this.markPaidDate.format('MMM D, YYYY')}`
        if (this.paidDate) return `Paid ${this.paidDate.format('MMM D, YYYY')}`
        if (this.checkinDate) return `Checked in ${this.checkinDate.format('MMM D, YYYY')}`
        if (this.checkoutDate) return `Checked out ${this.checkoutDate.format('MMM D, YYYY')}`
        if (this.id) return `Confirmed ${this.confirmedDate!.format('MMM D, YYYY')}`
        return 'Already confirmed'
    }

    get checkinStatus(): string | undefined {
        const b = this.booking
        if (b) {
            const date = b.date
            const endDate = b.endDate
            const checkinDate = this.checkinDate
            if (!checkinDate && endDate?.isBefore(moment()) && this.allowCheckin) return 'No show'
            if (checkinDate && checkinDate.isSameOrBefore(moment(date).add(10, 'minutes')))
                return 'On time'
            return checkinDate ? 'Late' : undefined
        }
        return undefined
    }

    get record(): BaseRecord | undefined {
        return this.booking ?? this.invoice ?? this.aPackage ?? this.subscription
    }

    async allowCheckinBy(me: User): Promise<boolean> {
        const date = moment()
        const booking = this.booking
        if (!booking || !booking.confirmedDate || booking.owner?.id !== me.owner?.id) return false
        const payee = await RecordFactory.getInstance().fetchPayee(this.person?.id ?? '', false)
        if (
            payee &&
            payee.showCheckin &&
            date.isAfter(moment(booking.date).subtract(30, 'minutes')) &&
            date.isBefore(moment(booking.endDate).add(30, 'minutes'))
        )
            return (
                (me.perm >= User.PERM_ROLE_ADMIN &&
                    (!booking.jobCheckinDate || !booking.jobCheckoutDate)) ||
                (me.perm < User.PERM_ROLE_ADMIN &&
                    this.person?.id === me.id &&
                    (!this.checkinDate || !this.checkoutDate))
            )
        return false
    }

    static checkInReminderStrings(): Option[] {
        const strings: Option[] = []
        strings.push(new Option(undefined, { id: '', label: "Don't remind" }))
        strings.push(new Option(undefined, { id: '5', label: '5 minutes' }))
        strings.push(new Option(undefined, { id: '10', label: '10 minutes' }))
        strings.push(new Option(undefined, { id: '15', label: '15 minutes' }))
        strings.push(new Option(undefined, { id: '30', label: '30 minutes' }))
        strings.push(new Option(undefined, { id: '45', label: '45 minutes' }))
        strings.push(new Option(undefined, { id: '60', label: '60 minutes' }))
        return strings
    }

    get typeString(): string {
        if (!this.type) return ''

        return (
            this.type
                .replace('booking', 'appointment')
                .replace('lesson', 'class')
                .charAt(0)
                .toUpperCase() + this.type.slice(1)
        )
    }

    get subtypeString(): string | null {
        if (this.subtype === null) {
            return null
        }

        switch (this.subtype) {
            case 'checkin':
                return 'Check-in'
            case 'checkout':
                return 'Check-out'
        }
    }

    deserialize(data: any) {
        super.deserialize(data)
        if (data.owner) this.owner = BaseRecord.fromSerializer(data.owner, User)
        if (data.client) this.client = BaseRecord.fromSerializer(data.client, User)
        if (data.person) this.person = BaseRecord.fromSerializer(data.person, User)
        if (data.creator) this.creator = BaseRecord.fromSerializer(data.creator, User)
        if (data.booking) this.booking = BaseRecord.fromSerializer(data.booking, Booking)
        if (data.lesson) this.lesson = BaseRecord.fromSerializer(data.lesson, Lesson)
        if (data.invoice) this.invoice = BaseRecord.fromSerializer(data.invoice, Invoice)
        if (data.aPackage) this.aPackage = BaseRecord.fromSerializer(data.aPackage, Package)
        if (data.subscription)
            this.subscription = BaseRecord.fromSerializer(data.subscription, Subscription)
        if (data.checkinBy) this.checkinBy = BaseRecord.fromSerializer(data.checkinBy, User)
        if (data.checkoutBy) this.checkoutBy = BaseRecord.fromSerializer(data.checkoutBy, User)
        if (data.checkinDate) this.checkinDate = moment(data.checkinDate)
        if (data.checkoutDate) this.checkoutDate = moment(data.checkoutDate)
        if (data.checkinImage)
            this.checkinImage = BaseRecord.fromSerializer(data.checkinImage, Image)
        if (data.checkoutImage)
            this.checkoutImage = BaseRecord.fromSerializer(data.checkoutImage, Image)
        if (data.date) this.date = moment(data.date)
        if (data.confirmedDate) this.confirmedDate = moment(data.confirmedDate)
        if (data.paidDate) this.paidDate = moment(data.paidDate)
        if (data.markPaidDate) this.markPaidDate = moment(data.markPaidDate)
        if (data.cancelledDate) this.cancelledDate = moment(data.cancelledDate)
        if (data.availableDate) this.availableDate = moment(data.availableDate)
        if (data.lastModifiedDate) this.lastModifiedDate = moment(data.lastModifiedDate)
    }

    static fk: FKType = {
        users: ['owner', 'client', 'person', 'creator', 'checkinBy', 'checkoutBy'],
        bookings: ['booking'],
        lessons: ['lesson'],
        invoices: ['invoice'],
        packages: ['aPackage'],
        subscriptions: ['subscription'],
    }

    static dataType(): DataType {
        return 'jobs'
    }

    jsonObject(): any {
        const dict = super.jsonObject()
        dict['person'] = this.person?.id
        if (this.id) {
            dict['amount'] = this.amount ?? ''
            if (this.checkinDate)
                dict['checkin_date'] = this.checkinDate.format('YYYY-MM-DD HH:mm:ss')
            if (this.checkoutDate)
                dict['checkout_date'] = this.checkoutDate.format('YYYY-MM-DD HH:mm:ss')
        }
        return dict
    }

    get client(): User | undefined {
        return this.getForeignRecord('client', User)
    }
    set client(client: User | undefined) {
        this.setForeignRecord('client', client)
    }
    get owner(): User | undefined {
        return this.getForeignRecord('owner', User)
    }
    set owner(owner: User | undefined) {
        this.setForeignRecord('owner', owner)
    }
    get person(): User | undefined {
        return this.getForeignRecord('person', User)
    }
    set person(person: User | undefined) {
        this.setForeignRecord('person', person)
    }
    get creator(): User | undefined {
        return this.getForeignRecord('creator', User)
    }
    set creator(creator: User | undefined) {
        this.setForeignRecord('creator', creator)
    }
    get booking(): Booking | undefined {
        return this.getForeignRecord('booking', Booking)
    }
    set booking(booking: Booking | undefined) {
        this.setForeignRecord('booking', booking)
    }
    get invoice(): Invoice | undefined {
        return this.getForeignRecord('invoice', Invoice)
    }
    set invoice(invoice: Invoice | undefined) {
        this.setForeignRecord('invoice', invoice)
    }
    get lesson(): Lesson | undefined {
        return this.getForeignRecord('lesson', Lesson)
    }
    set lesson(lesson: Lesson | undefined) {
        this.setForeignRecord('lesson', lesson)
    }
    get aPackage(): Package | undefined {
        return this.getForeignRecord('package', Package)
    }
    set aPackage(aPackage: Package | undefined) {
        this.setForeignRecord('package', aPackage)
    }
    get subscription(): Subscription | undefined {
        return this.getForeignRecord('subscription', Subscription)
    }
    set subscription(subscription: Subscription | undefined) {
        this.setForeignRecord('subscription', subscription)
    }
    get checkinBy(): User | undefined {
        return this.getForeignRecord('checkin_by', User)
    }
    set checkinBy(checkinBy: User | undefined) {
        this.setForeignRecord('checkin_by', checkinBy)
    }
    get checkoutBy(): User | undefined {
        return this.getForeignRecord('checkout_by', User)
    }
    set checkoutBy(checkoutBy: User | undefined) {
        this.setForeignRecord('checkout_by', checkoutBy)
    }
}
